import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, CalendarDays, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const Contact = () => {
  const { t } = useLanguage();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [demoForm, setDemoForm] = useState({
    name: '',
    email: '',
    company: '',
    date: null,
    time: '',
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success(t('contact.form.success'));
      setContactForm({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      toast.error(t('contact.form.error'));
    } finally {
      setContactLoading(false);
    }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setDemoLoading(true);
    try {
      await axios.post(`${API}/demo-request`, {
        ...demoForm,
        date: demoForm.date ? format(demoForm.date, 'yyyy-MM-dd') : null,
      });
      toast.success(t('contact.demo.success'));
      setDemoForm({ name: '', email: '', company: '', date: null, time: '' });
    } catch (error) {
      toast.error(t('contact.form.error'));
    } finally {
      setDemoLoading(false);
    }
  };

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#F5F5F7]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-semibold text-[#1D1D1F] tracking-tight leading-[1.1] mb-6 font-['Plus_Jakarta_Sans']">
              {t('contact.title')}
            </h1>
            <p className="text-lg md:text-xl text-[#86868B] leading-relaxed">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Forms */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div {...fadeUp} className="lg:col-span-1">
              <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-8 font-['Plus_Jakarta_Sans']">
                {t('contact.info.address')}
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#007AFF10] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1D1D1F] mb-1">{t('contact.info.address')}</p>
                    <p className="text-[#86868B]">
                      20-22 Wenlock Road<br />
                      London N1 7GU, UK
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#34C75910] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#34C759]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1D1D1F] mb-1">{t('contact.info.phone')}</p>
                    <a
                      href="tel:+442921680168"
                      className="text-[#86868B] hover:text-[#007AFF] transition-colors"
                    >
                      +44 (0)29 2168 0168
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#FF950010] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#FF9500]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1D1D1F] mb-1">{t('contact.info.email')}</p>
                    <a
                      href="mailto:contact@corpvictory.com"
                      className="text-[#86868B] hover:text-[#007AFF] transition-colors"
                    >
                      contact@corpvictory.com
                    </a>
                  </div>
                </div>
              </div>

              {/* EU Badge */}
              <div className="mt-12 p-6 rounded-2xl bg-[#F5F5F7]">
                <p className="text-sm font-medium text-[#1D1D1F] mb-2">European Focus</p>
                <p className="text-sm text-[#86868B]">
                  We serve businesses across the European Union with GDPR-compliant solutions.
                </p>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="lg:col-span-1"
            >
              <div className="bg-[#F5F5F7] p-8 rounded-3xl">
                <h2 className="text-2xl font-semibold text-[#1D1D1F] mb-6 font-['Plus_Jakarta_Sans']">
                  Send a Message
                </h2>
                <form onSubmit={handleContactSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="contact-name" className="text-[#1D1D1F] mb-2 block">
                      {t('contact.form.name')}
                    </Label>
                    <Input
                      id="contact-name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7] focus:ring-[#007AFF] bg-white"
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="text-[#1D1D1F] mb-2 block">
                      {t('contact.form.email')}
                    </Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7] focus:ring-[#007AFF] bg-white"
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-company" className="text-[#1D1D1F] mb-2 block">
                      {t('contact.form.company')}
                    </Label>
                    <Input
                      id="contact-company"
                      value={contactForm.company}
                      onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                      className="h-12 rounded-xl border-[#D2D2D7] focus:ring-[#007AFF] bg-white"
                      data-testid="contact-company-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-message" className="text-[#1D1D1F] mb-2 block">
                      {t('contact.form.message')}
                    </Label>
                    <Textarea
                      id="contact-message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="min-h-[120px] rounded-xl border-[#D2D2D7] focus:ring-[#007AFF] bg-white resize-none"
                      required
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={contactLoading}
                    className="w-full bg-[#1D1D1F] text-white hover:bg-black rounded-full py-3 text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98] inline-flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {contactLoading ? 'Sending...' : t('contact.form.submit')}
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Demo Request Form */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="bg-[#1D1D1F] p-8 rounded-3xl">
                <h2 className="text-2xl font-semibold text-white mb-2 font-['Plus_Jakarta_Sans']">
                  {t('contact.demo.title')}
                </h2>
                <p className="text-[#86868B] mb-6">{t('contact.demo.subtitle')}</p>
                <form onSubmit={handleDemoSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="demo-name" className="text-white mb-2 block">
                      {t('contact.form.name')}
                    </Label>
                    <Input
                      id="demo-name"
                      value={demoForm.name}
                      onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                      className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-[#007AFF]"
                      required
                      data-testid="demo-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="demo-email" className="text-white mb-2 block">
                      {t('contact.form.email')}
                    </Label>
                    <Input
                      id="demo-email"
                      type="email"
                      value={demoForm.email}
                      onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                      className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-[#007AFF]"
                      required
                      data-testid="demo-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="demo-company" className="text-white mb-2 block">
                      {t('contact.form.company')}
                    </Label>
                    <Input
                      id="demo-company"
                      value={demoForm.company}
                      onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })}
                      className="h-12 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:ring-[#007AFF]"
                      data-testid="demo-company-input"
                    />
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">
                      {t('contact.demo.preferredDate')}
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full h-12 rounded-xl bg-white/10 border-white/20 text-white hover:bg-white/20 justify-start"
                          data-testid="demo-date-btn"
                        >
                          <CalendarDays className="w-4 h-4 mr-2" />
                          {demoForm.date ? format(demoForm.date, 'PPP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={demoForm.date}
                          onSelect={(date) => setDemoForm({ ...demoForm, date })}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">
                      {t('contact.demo.preferredTime')}
                    </Label>
                    <Select
                      value={demoForm.time}
                      onValueChange={(value) => setDemoForm({ ...demoForm, time: value })}
                    >
                      <SelectTrigger
                        className="h-12 rounded-xl bg-white/10 border-white/20 text-white"
                        data-testid="demo-time-select"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="submit"
                    disabled={demoLoading}
                    className="w-full bg-[#007AFF] text-white hover:bg-[#0051A8] rounded-full py-3 text-base font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
                    data-testid="demo-submit-btn"
                  >
                    {demoLoading ? 'Submitting...' : t('contact.demo.submit')}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
